/**
 * Messaging Attachments Utility
 * Handles file and audio uploads for messaging
 */

import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/firebase/config';

export interface AttachmentUploadResult {
  url: string;
  type: 'image' | 'file' | 'audio' | 'video';
  name: string;
  size?: number;
}

/**
 * Upload a file attachment (image or file) for messaging
 */
export async function uploadMessageAttachment(
  file: File,
  conversationId: string,
  userId: string
): Promise<AttachmentUploadResult> {
  // Validate file size before attempting upload (videos can be larger)
  const { validateFileSizeByType } = await import('./fileSizeValidation');
  validateFileSizeByType(file);

  if (!storage) {
    throw new Error('Firebase Storage not initialized');
  }

  const timestamp = Date.now();
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const fileExtension = file.name.split('.').pop() || '';
  
  // Determine type
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');
  const type = isImage ? 'image' : isVideo ? 'video' : 'file';
  
  // Create storage path
  const storagePath = `messages/${conversationId}/${timestamp}_${sanitizedFileName}`;
  const storageRef = ref(storage, storagePath);

  // Upload file
  await uploadBytes(storageRef, file, {
    contentType: file.type,
  });

  // Get download URL
  const url = await getDownloadURL(storageRef);

  return {
    url,
    type,
    name: file.name,
    size: file.size,
  };
}

/**
 * Upload an audio recording for messaging
 */
export async function uploadAudioRecording(
  audioBlob: Blob,
  conversationId: string,
  userId: string
): Promise<AttachmentUploadResult> {
  if (!storage) {
    throw new Error('Firebase Storage not initialized');
  }

  const timestamp = Date.now();
  const storagePath = `messages/${conversationId}/audio_${timestamp}.webm`;
  const storageRef = ref(storage, storagePath);

  // Upload audio blob
  await uploadBytes(storageRef, audioBlob, {
    contentType: 'audio/webm',
  });

  // Get download URL
  const url = await getDownloadURL(storageRef);

  return {
    url,
    type: 'audio',
    name: `Audio Recording ${new Date(timestamp).toLocaleTimeString()}`,
    size: audioBlob.size,
  };
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

